/*
 * SUBTITULOS VIVOS DE LA PANTALLA DE AJUSTES · incremento 16/17
 * (RF-229, RN-643, RN-644, D-342; patron vivo-con-respaldo de RF-216 / D-315).
 *
 * POR QUE VIVE FUERA DE LA PANTALLA. Son reglas de texto con casos —dos personas,
 * una sola, una sin acceso; conteos resueltos, cargando o fallidos— y la pantalla
 * no es el sitio para probarlas. Aqui son funciones puras: entran datos ya
 * recibidos, sale una cadena en castellano. Ninguna habla con el servidor.
 *
 * LA REGLA QUE MANDA EN LAS DOS: NUNCA UN HUECO Y NUNCA UN "0" A MEDIAS. Si el
 * dato no esta resuelto o no alcanza para una frase verdadera, estas funciones
 * devuelven `null` y la pantalla pinta su texto de respaldo. Devolver una frase a
 * medias seria peor que no decir nada: diria algo falso.
 */

/** Respaldo de Categorias: el texto estatico de hoy (`Ajustes.jsx`, antes del 16). */
export const RESPALDO_DE_CATEGORIAS = 'Lista plana por tipo, sin subcategorías';

/*
 * Respaldo de Personas del hogar. Es el texto de hoy SIN su conteo: el conteo era
 * justo el dato impreciso que RN-643 viene a corregir —contaba personas sin
 * distinguir quien tiene acceso—, asi que no se conserva ni como respaldo. Lo que
 * queda es la enumeracion de lo que se puede hacer ahi, palabra por palabra.
 */
export const RESPALDO_DE_PERSONAS = 'Alta, nombre, restablecer contraseña, activar y desactivar';

/**
 * Subtitulo del renglon «Personas del hogar» (RN-643).
 *
 * El dato ya viaja en el contexto de sesion: trae a TODAS las personas del hogar
 * con su nombre y su estado de acceso. Sin contrato nuevo.
 *
 * @param personas lista de `{ nombre, activo }` tal como llega en la sesion.
 * @returns la frase, o `null` si no alcanza para una frase verdadera.
 */
export function subtituloDePersonasDelHogar(personas) {
  if (!Array.isArray(personas) || personas.length === 0) return null;

  const nombres = personas.map((persona) => (persona?.nombre ?? '').trim());
  // Sin nombre no hay frase que nombre a nadie: se cae al respaldo.
  if (nombres.some((nombre) => nombre === '')) return null;

  if (personas.length === 1) return `${nombres[0]} · aún sin segunda persona`;

  if (personas.length === 2) {
    const sinAcceso = personas.filter((persona) => persona?.activo === false);
    if (sinAcceso.length === 0) return `${nombres[0]} y ${nombres[1]} · las dos con acceso activo`;
    if (sinAcceso.length === 1) {
      return `${nombres[0]} y ${nombres[1]} · el acceso de ${(sinAcceso[0].nombre ?? '').trim()} está desactivado`;
    }
    // Las dos sin acceso es imposible (siempre queda una activa, y quien mira
    // esta pantalla esta en sesion): si llegara, no se inventa una frase.
    return null;
  }

  // El hogar es de dos personas por diseno. Si alguna vez fueran mas, RN-643 no
  // tiene variante escrita y aqui no se redacta una.
  return null;
}

/**
 * Subtitulo del renglon «Categorias» (RN-644, D-342): los conteos reales de
 * categorias ACTIVAS. Una cifra fija mentiria al primer cambio del catalogo.
 *
 * @param conteo `{ gasto, ingreso }` ya resuelto, o nulo mientras carga / si fallo.
 * @returns la frase, o `null` para que la pantalla use su respaldo.
 */
export function subtituloDeCategorias(conteo) {
  if (!conteo) return null;
  const { gasto, ingreso } = conteo;
  if (!Number.isInteger(gasto) || !Number.isInteger(ingreso)) return null;
  if (gasto < 0 || ingreso < 0) return null;

  return `${gasto} de gasto · ${ingreso} de ingreso`;
}

/**
 * Cuenta las dos listas que ya devuelve el catalogo. Se queda aqui para que la
 * pantalla no interprete respuestas: si alguna de las dos no es una lista, no hay
 * conteo y manda el respaldo.
 */
export function conteoDeCategoriasActivas(deGasto, deIngreso) {
  if (!Array.isArray(deGasto) || !Array.isArray(deIngreso)) return null;
  return { gasto: deGasto.length, ingreso: deIngreso.length };
}
